import { memo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Video, GripVertical } from "lucide-react";
import { Spinner } from "@/components/ui";
import { UploadProgressOverlay } from "@/components/common/UploadProgressOverlay";
import { cn } from "@/lib/utils";
import { useStorageObjectUrl } from "@/hooks/useStorageObjectUrl";
import type { ThumbnailProps } from "./types";

export const Thumbnail = memo(function Thumbnail({
  src,
  alt,
  isVideo,
  isUploading,
  upload,
  isSelected,
  isDragging,
  isDragOver,
  editable,
  onClick,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: ThumbnailProps) {
  const { t: tCommon } = useTranslation("common");
  const storage = useStorageObjectUrl(src);
  const resolvedSrc =
    storage.status === "ready" ? storage.url : undefined;
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDragLeave = (e: React.DragEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.relatedTarget as Node)
    ) {
      onDragLeave();
    }
  };

  return (
    <div
      ref={containerRef}
      draggable={editable && !isUploading}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={handleDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={cn(
        "group relative h-16 w-20 flex-shrink-0 cursor-pointer rounded-lg transition-all",
        isSelected && "ring-2 ring-primary-500 ring-offset-2 ring-offset-surface-0",
        !isSelected && "opacity-70 hover:opacity-100",
        isDragging && "scale-95 opacity-50",
        isDragOver && "ring-2 ring-accent-500",
      )}
    >
      {!resolvedSrc && isUploading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-surface-200">
          <Spinner size="sm" variant="muted" loadingLabel={tCommon("loading")} />
        </div>
      ) : null}

      {resolvedSrc ? (
        <img
          src={resolvedSrc}
          alt={alt}
          className={cn(
            "h-full w-full rounded-lg object-cover",
            isUploading && "opacity-60",
          )}
          draggable={false}
        />
      ) : null}

      {upload ? <UploadProgressOverlay upload={upload} compact /> : null}

      {isVideo && !isUploading ? (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/30">
          <Video className="h-5 w-5 text-white" />
        </div>
      ) : null}

      {editable && !isUploading ? (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 opacity-0 transition-all group-hover:bg-black/40 group-hover:opacity-100">
          <GripVertical className="h-5 w-5 text-white" />
        </div>
      ) : null}
    </div>
  );
});
